
import {useState} from "react"
import {
    LayoutDashboard,
    Users,
    FileText,
    Settings,
    Building2,
    Shield,
    UserCog,
    MapPin,
    Tag,
    DollarSignIcon,
    ChevronDown,
    type LucideIcon,
} from "lucide-react"
import {Link, usePage} from "@inertiajs/react"
import {cn} from "@/lib/utils"

interface NavItem {
    href: string
    label: string
    icon: LucideIcon
    superUser?: boolean
}

const topLevelNavItems: NavItem[] = [
    {
        href: "/dashboard",
        label: "Tableau de bord",
        icon: LayoutDashboard,
    },
    {
        href: "/tax_payers",
        label: "Gestion des contribuables",
        icon: Users,
    },
    {
        href: "/collect_sessions",
        label: "Sessions de collecte",
        icon: DollarSignIcon,
    },
]

const communityNavItem: NavItem = {
    href: "/communities",
    label: "Communautés",
    icon: Building2,
    superUser: true,
}

const adminNavItems: NavItem[] = [
    {
        href: "/admin/areas",
        label: "Gestion des zones",
        icon: MapPin,
    },
    {
        href: "/admin/categories",
        label: "Gestion des catégories",
        icon: Tag,
    },
    {
        href: "/admin/financial_audits",
        label: "Audit financier",
        icon: FileText,
    },
    {
        href: "/admin/payment_audits",
        label: "Audit des paiements",
        icon: DollarSignIcon,
    },
    {
        href: "/admin",
        label: "Administration système",
        icon: Settings,
    },
    {
        href: "/admin/users",
        label: "Gestion des utilisateurs",
        icon: UserCog,
    },
]

const isNavItemActive = (pathname: string, href: string) => {
    if (pathname === href) {
        return true
    }

    if (href === "/admin") {
        return false
    }

    return pathname.startsWith(`${href}/`)
}

export function AppSidebar() {
    const pageUrl = usePage().url
    const pathname = pageUrl.split(/[?#]/)[0] ?? pageUrl
    const hasActiveAdminItem = adminNavItems.some((item) => isNavItemActive(pathname, item.href))
    const [settingsOpen, setSettingsOpen] = useState(hasActiveAdminItem)
    const settingsExpanded = settingsOpen || hasActiveAdminItem

    const renderNavLink = (item: NavItem, nested = false) => {
        const Icon = item.icon
        const isActive = isNavItemActive(pathname, item.href)

        return (
            <Link
                key={item.href}
                href={item.href}
                className={cn(
                    "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                    item.superUser && "mt-6 border-t border-border pt-6",
                    nested && "pl-8 text-xs"
                )}
            >
                <Icon className="h-4 w-4" />
                {item.label}
                {item.superUser && (
                    <span className="ml-auto rounded bg-accent px-1.5 py-0.5 text-xs font-medium text-accent-foreground">
                        Super
                    </span>
                )}
            </Link>
        )
    }

    return (
        <aside className="w-64 border-r border-border bg-card">
            <div className="flex h-16 items-center border-b border-border px-6">
                <Shield className="mr-2 h-5 w-5 text-primary" />
                <h1 className="text-lg font-semibold text-foreground">Fiscalité municipale</h1>
            </div>
            <nav className="space-y-1 p-4">
                {topLevelNavItems.map((item) => renderNavLink(item))}
                <div className="space-y-1">
                    <button
                        type="button"
                        aria-expanded={settingsExpanded}
                        aria-controls="sidebar-admin-links"
                        onClick={() => setSettingsOpen((open) => !open)}
                        className={cn(
                            "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                            hasActiveAdminItem
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                        )}
                    >
                        <Settings className="h-4 w-4" />
                        Paramètres
                        <ChevronDown
                            className={cn(
                                "ml-auto h-4 w-4 transition-transform",
                                settingsExpanded && "rotate-180"
                            )}
                        />
                    </button>
                    {settingsExpanded && (
                        <div id="sidebar-admin-links" className="space-y-1 border-l border-border pl-2">
                            {adminNavItems.map((item) => renderNavLink(item, true))}
                        </div>
                    )}
                </div>
                {renderNavLink(communityNavItem)}
            </nav>
            <div className="absolute bottom-0 w-64 border-t border-border bg-card p-4">
                <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                        AD
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">Utilisateur admin</p>
                        <p className="text-xs text-muted-foreground">Super administrateur</p>
                    </div>
                </div>
            </div>
        </aside>
    )
}
